import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { RichTextEditor } from 'components/rich-text-editor/rich-text-editor';
import { Modal, Button } from '@good/ui/core';

interface TermsConditionsEditorProps {
  contentHtml: string;
  minHeight?: string;
  disabled?: boolean;
}

export interface TermsConditionsEditorRef {
  validate: () => { isValid: boolean; content: string | null };
}

const TermsConditionsEditor = forwardRef<TermsConditionsEditorRef, TermsConditionsEditorProps>(
  ({ contentHtml, minHeight = '200px', disabled = false }, ref) => {
    const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
    const [content, setContent] = useState(contentHtml || '');

    useEffect(() => {
      setContent(contentHtml || '');
    }, [contentHtml]);

    const handleFullscreenToggle = () => {
      setIsEditorFullscreen(!isEditorFullscreen);
    };

    const handleContentChange = (newContent: string) => {
      setContent(newContent);
    };

    const validate = () => {
      const plainText = content.replace(/<[^>]*>/g, '').trim();
      return {
        isValid: true,
        content: plainText.length === 0 ? null : content,
      };
    };

    useImperativeHandle(ref, () => ({
      validate,
    }));

    const editorHeight = isEditorFullscreen
      ? window.innerHeight - 200
      : Number.parseInt(minHeight.replace('px', ''), 10);

    return (
      <>
        <div className='relative'>
          <RichTextEditor
            value={content}
            onChange={handleContentChange}
            placeholder='Please fill the terms & conditions here.'
            height={editorHeight}
            disabled={disabled}
          />
          {!disabled && (
            <button
              type='button'
              onClick={handleFullscreenToggle}
              className='absolute top-2 right-2 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-300'
              title='Open in fullscreen'
            >
              ⛶
            </button>
          )}
        </div>

        <Modal
          opened={isEditorFullscreen}
          onClose={handleFullscreenToggle}
          title='Terms & Conditions Editor'
          size='lg'
          maxHeight='90vh'
          confirmProps={{
            children: 'Save & Close',
            onClick: handleFullscreenToggle,
          }}
          cancelProps={{
            children: 'Cancel',
            onClick: handleFullscreenToggle,
          }}
        >
          <RichTextEditor
            value={content}
            onChange={handleContentChange}
            placeholder='Please fill the terms & conditions here.'
            height={window.innerHeight - 300}
            disabled={disabled}
          />
        </Modal>
      </>
    );
  },
);

TermsConditionsEditor.displayName = 'TermsConditionsEditor';

export default TermsConditionsEditor;
